/** @jsxImportSource @emotion/react */
import { CircularProgress, Container, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { useTranslation } from 'react-i18next';
import { IReportResultGroupsModel, IReportResultModel, IReportResultRowsModel } from '../../../service/model/IReportResultModel';
import { useParams } from 'react-router-dom';
import ReportResultService from '../../../service/ReportResultService';
import ErrorMessageContainer from '../../../common/components/ErrorMessageContainer';
import EmptyTableMessageContainer from '../../../common/components/EmptyTableMessageContainer';

interface IProps {
    year?: number,
}
interface IGroups {
    reportResultRow: IReportResultGroupsModel
}
interface IRow {
    row: IReportResultRowsModel,
    subGroup: number,
    open: boolean | null,
}
interface ISubRows {
    open: boolean,
    subRows: IReportResultRowsModel[],
}

const TableResult = (props: IProps): JSX.Element => {

    const { t } = useTranslation();

    const { organizationId } = useParams();

    const [tableGroups, setTableGroups] = useState<IReportResultGroupsModel[]>([]);
    const [tableGroupsErrors, setTableGroupsError] = useState(false);
    const [loader, setLoader] = useState(true);


    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        setLoader(true);

        const service = new ReportResultService();

        const getGroups = async () => {
            const result = await service.getReportResults(organizationId, props.year);
            if (result.hasErrors) {
                setTableGroupsError(true);
            } else {
                setTableGroupsError(false);
                if (isMounted) {
                    setTableGroups(result.value.groups);
                }
            }
            setLoader(false);
        };

        getGroups();

        return () => { isMounted = false };

    }, [organizationId, props.year]);

    const CreateSegment = (props: IGroups): JSX.Element => {
        return <>
            <TableRow>
                <TableCell colSpan={16} ><Typography variant="h6">{props.reportResultRow.heading}</Typography></TableCell>
            </TableRow>
            {props.reportResultRow.rows.map(e => {
                return <CreateRow row={e} subGroup={-1} open={null} /> 
            })}
        </>
    }

    const CreateRow = (props: IRow): JSX.Element => {
                
        const [open, setOpen] = useState(false);

        let subGroup = props.subGroup + 1;

        return <>
            <TableRow sx={{
                '& > *': { borderBottom: 'unset'},
                display: props.open === null || props.open ? "table-row" : "none"
            }}>
                <TableCell>
                    <IconButton sx={{ display: props.row.rows && props.row.rows.length > 0 ? "block" : "none" }}
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">{" * ".repeat(subGroup) + props.row.description}</TableCell>
                {props.row.periods.map(q => {
                    return <TableCell align="right">
                        {new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(q)}
                    </TableCell>
                })}
                <TableCell align="right">
                    {new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(props.row.total)}
                </TableCell>
            </TableRow>
            {props.row.rows && props.row.rows.length > 0 && props.row.rows.map(e => {
                return <>
                    <CreateRow row={e} open={open} subGroup={subGroup} />
                </>
            })}
            
            {/*<CreateSubRows open={open} subRows={props.row.rows} />*/}
        </>
    }

    //const CreateSubRows = (props: ISubRows): JSX.Element => {

    //    return <>
    //        {props.subRows.map(e => {
    //            return <>
    //                <TableRow sx={{ display: props.open ? "table-row" : "none" }}>
    //                    <TableCell></TableCell>
    //                    <TableCell component="th" scope="row">{e.description}</TableCell>
    //                    {e.periods.map(q => {
    //                        return <TableCell align="right">
    //                            {new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(q)}
    //                        </TableCell>
    //                    })}
    //                    <TableCell align="right">
    //                        {new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(e.total)}
    //                    </TableCell>
    //                </TableRow>
    //            </>
    //        })}
    //    </>

    //}

    if (loader) {
        return <CircularProgress />
    } else if (tableGroupsErrors) {
        return <ErrorMessageContainer />
    } else if (tableGroups.length < 1) {
        return <EmptyTableMessageContainer/>
    } else {
        return <TableContainer sx={{ height: "100%" }}>
            <Table aria-label="collapsible table" stickyHeader sx={{ width: "100%", height: "100%" }}>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell>{t("reports.description")}</TableCell>
                        <TableCell align="right">{t("dateSearch.jan")}</TableCell>
                        <TableCell align="right">{t("dateSearch.feb")}</TableCell>
                        <TableCell align="right">{t("dateSearch.march")}</TableCell>
                        <TableCell align="right">{t("dateSearch.apr")}</TableCell>
                        <TableCell align="right">{t("dateSearch.may")}</TableCell>
                        <TableCell align="right">{t("dateSearch.june")}</TableCell>
                        <TableCell align="right">{t("dateSearch.july")}</TableCell>
                        <TableCell align="right">{t("dateSearch.aug")}</TableCell>
                        <TableCell align="right">{t("dateSearch.sep")}</TableCell>
                        <TableCell align="right">{t("dateSearch.oct")}</TableCell>
                        <TableCell align="right">{t("dateSearch.nov")}</TableCell>
                        <TableCell align="right">{t("dateSearch.dec")}</TableCell>
                        <TableCell align="right">{t("reports.corrections")}</TableCell>
                        <TableCell align="right">{t("reports.total")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableGroups.map(e => {
                        return <CreateSegment reportResultRow={e} />
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    }
}

export default TableResult;